import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { MARKETS, flagFor } from "@/lib/constants";
import { Search, TriangleAlert, TrendingUp, TrendingDown, Sparkles, Download, CheckCircle2, AlertTriangle, XCircle, Building2 } from "lucide-react";
import { toast } from "sonner";

function Metric({ label, value, mono }) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">{label}</p>
      <p className={`mt-1 text-stone-900 ${mono ? "tabular text-lg font-medium" : "text-base font-medium"}`}>{value ?? "—"}</p>
    </div>
  );
}

function ScoreIcon({ v }) {
  const n = Number(v);
  if (n >= 8) return <CheckCircle2 size={16} className="text-emerald-600" />;
  if (n >= 6) return <AlertTriangle size={16} className="text-amber-600" />;
  return <XCircle size={16} className="text-rose-600" />;
}

function List({ title, items, tone }) {
  if (!items?.length) return null;
  const tones = { good: "border-emerald-200 bg-emerald-50/60 text-navy", bad: "border-rose-200 bg-rose-50/60 text-rose-700", neutral: "border-stone-200 bg-white text-stone-900" };
  return (
    <div className={`rounded-xl border p-5 ${tones[tone] || tones.neutral}`}>
      <p className="text-sm font-semibold">{title}</p>
      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-stone-700">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
}

export default function StockResearch() {
  const [params] = useSearchParams();
  const [market, setMarket] = useState(params.get("market") || "Global");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const doSearch = useCallback(async (query, mkt) => {
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: query, market: mkt } });
      setResults(data.results || []);
    } catch (e) { console.error("Search failed:", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { doSearch("", market); }, [market, doSearch]);

  const analyze = async (query) => {
    setAnalyzing(true); setReport(null);
    try {
      const { data } = await api.post("/company/research", { query, market });
      setReport(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not research this company.");
    } finally { setAnalyzing(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/report/pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report }),
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Meridian_${(report.ticker || "report").replace(".", "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF."); }
    finally { setDownloading(false); }
  };

  return (
    <main className="mx-auto max-w-6xl px-6 py-10 md:px-8">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">AI Company Research</p>
      <h1 className="mt-2 font-head text-3xl font-medium tracking-tight text-stone-900 sm:text-4xl">Research any public company</h1>
      <p className="mt-2 max-w-2xl text-stone-600">Search by name, ticker, exchange, country, industry or sector. Get a full report with a "Before You Buy" scorecard.</p>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="stock-market-selector">
        {MARKETS.map((m) => (
          <button key={m.id} data-testid={`stock-market-${m.id}`} onClick={() => setMarket(m.id)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
              market === m.id ? "border-navy bg-navy text-white" : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
            }`}>{m.flag} {m.label}</button>
        ))}
      </div>

      <div className="mt-5 flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
          <input data-testid="stock-search-input" value={q}
            onChange={(e) => { setQ(e.target.value); doSearch(e.target.value, market); }}
            onKeyDown={(e) => e.key === "Enter" && q && analyze(q)}
            placeholder="e.g. Apple, MTNN.NG, London Stock Exchange, Banks…"
            className="h-12 w-full rounded-lg border border-stone-200 bg-white pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
        </div>
        <button data-testid="stock-analyze-btn" disabled={!q || analyzing} onClick={() => analyze(q)}
          className="flex h-12 items-center gap-2 rounded-lg bg-navy px-5 text-sm font-medium text-white transition-colors hover:bg-navyd disabled:opacity-60">
          <Sparkles size={16} strokeWidth={1.5} /> Research
        </button>
      </div>

      {analyzing && (
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-6" data-testid="analyzing">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-stone-300 border-t-navy" />
          <p className="text-sm text-stone-600">Building deep research report with GPT-5.5…</p>
        </div>
      )}

      {report && !analyzing && <ResearchReport report={report} onDownload={downloadPdf} downloading={downloading} ScoreIcon={ScoreIcon} MetricCmp={Metric} ListCmp={List} />}

      {!report && (
        <div className="mt-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
            {loading ? "Searching…" : `${results.length} matches`}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {results.map((r, i) => (
              <button key={r.ticker || i} data-testid={`result-${i}`} onClick={() => analyze(r.ticker)}
                className="group flex items-start justify-between rounded-xl border border-stone-200 bg-white p-4 text-left transition-all hover:border-navy">
                <div>
                  <p className="font-medium text-stone-900">{flagFor(r.country)} {r.name}</p>
                  <p className="mt-0.5 text-xs text-stone-500">{r.exchange} · {r.sector}</p>
                </div>
                <span className="tabular rounded-md bg-stone-100 px-2 py-1 text-xs font-medium text-stone-700">{r.ticker}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </main>
  );
}

function verdictTone(v = "") {
  if (v.toLowerCase().startsWith("yes")) return "border-emerald-200 bg-emerald-50 text-navy";
  if (v.toLowerCase().startsWith("hold")) return "border-stone-200 bg-stone-50 text-stone-800";
  return "border-amber-200 bg-amber-50 text-amber-800";
}

function ResearchReport({ report, onDownload, downloading, ScoreIcon, MetricCmp, ListCmp }) {
  const p = report.profile || {};
  const sc = report.scorecard || {};
  const rec = report.recommendation || {};
  const fh = report.financial_health || {};
  const val = report.valuation || {};
  const swot = report.swot || {};
  const up = (report.day_change_pct || 0) >= 0;
  const scoreRows = [
    ["Business Quality", sc.business_quality], ["Financial Health", sc.financial_health],
    ["Valuation", sc.valuation], ["Management", sc.management],
    ["Competitive Advantage", sc.competitive_advantage], ["Dividend Quality", sc.dividend_quality],
  ];

  return (
    <div className="mt-6 space-y-5 animate-in-up" data-testid="stock-report">
      {report.warning && (
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="intl-warning">
          <TriangleAlert size={18} className="mt-0.5 shrink-0 text-amber-700" strokeWidth={1.5} />
          <p className="text-sm text-amber-800">{report.warning}</p>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl border border-stone-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-head text-2xl font-medium tracking-tight text-stone-900" data-testid="report-name">{flagFor(report.country)} {report.name}</h2>
              <span className="tabular rounded-md bg-stone-100 px-2 py-0.5 text-sm font-medium text-stone-700">{report.ticker}</span>
            </div>
            <p className="mt-1 text-sm text-stone-500">{report.exchange} · {report.country} · {report.sector} / {report.industry}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <p className="tabular text-2xl font-semibold text-stone-900" data-testid="report-price">
              {report.price != null ? `${report.currency} ${Number(report.price).toLocaleString()}` : "AI estimate"}
            </p>
            {report.live && (
              <span className={`inline-flex items-center gap-1 text-sm font-medium ${up ? "text-emerald-600" : "text-rose-600"}`}>
                {up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                {report.day_change_pct != null ? `${Number(report.day_change_pct).toFixed(2)}%` : ""} <span className="text-stone-400">live</span>
              </span>
            )}
            <button data-testid="export-pdf-btn" onClick={onDownload} disabled={downloading}
              className="flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-60">
              <Download size={15} strokeWidth={1.5} /> {downloading ? "Preparing…" : "Export PDF"}
            </button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <MetricCmp label="Market Cap" value={report.market_cap} mono />
          <MetricCmp label="Currency" value={report.currency} />
          <MetricCmp label="Sector" value={report.sector} />
          <MetricCmp label="Industry" value={report.industry} />
        </div>
      </div>

      {/* Before You Buy scorecard */}
      <div className="rounded-xl border border-stone-200 bg-white p-6" data-testid="scorecard">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-navy"><CheckCircle2 size={14} /> Before You Buy — Scorecard</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-500">Overall</span>
            <span className="tabular text-2xl font-semibold text-navy" data-testid="overall-score">{sc.overall_score ?? "—"}/100</span>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {scoreRows.map(([label, v]) => (
            <div key={label} className="flex items-center gap-3">
              <ScoreIcon v={v} />
              <span className="w-44 shrink-0 text-sm text-stone-700">{label}</span>
              <div className="h-2 flex-1 rounded-full bg-stone-100"><div className="h-2 rounded-full bg-emerald-700" style={{ width: `${(Number(v) || 0) * 10}%` }} /></div>
              <span className="tabular w-12 text-right text-sm font-medium text-stone-800">{v ?? "—"}/10</span>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-amber-600" />
            <span className="w-44 shrink-0 text-sm text-stone-700">Risk</span>
            <span className="text-sm font-medium text-stone-800">{sc.risk || "—"}</span>
          </div>
        </div>
        <div className={`mt-5 rounded-lg border p-4 ${verdictTone(rec.verdict)}`} data-testid="buy-verdict">
          <p className="text-xs font-semibold uppercase tracking-[0.15em]">Should I Buy?</p>
          <p className="mt-1 text-lg font-semibold">{rec.verdict || "—"}</p>
          <p className="mt-1 text-sm text-stone-700">{rec.reasoning}</p>
        </div>
      </div>

      {/* Company overview */}
      <div className="rounded-xl border border-stone-200 bg-white p-6">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-navy"><Building2 size={14} /> Company Overview</p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <MetricCmp label="Founded" value={p.founded} />
          <MetricCmp label="Founder" value={p.founder} />
          <MetricCmp label="CEO" value={p.ceo} />
          <MetricCmp label="Headquarters" value={p.headquarters} />
          <MetricCmp label="Employees" value={p.employees} />
        </div>
        {p.history && <p className="mt-4 text-stone-700">{p.history}</p>}
        {p.business_model && <p className="mt-3 text-sm text-stone-600"><span className="font-semibold text-stone-800">Business model: </span>{p.business_model}</p>}
        {p.products?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">{p.products.map((x, i) => <span key={i} className="rounded-full bg-stone-100 px-3 py-1 text-sm text-stone-700">{x}</span>)}</div>
        )}
        {p.revenue_breakdown?.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold text-stone-900">Revenue breakdown</p>
            <div className="space-y-2">
              {p.revenue_breakdown.map((r, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm"><span className="text-stone-600">{r.segment}</span><span className="tabular text-stone-500">{r.pct}</span></div>
                  <div className="mt-1 h-1.5 w-full rounded-full bg-stone-100"><div className="h-1.5 rounded-full bg-emerald-700" style={{ width: `${parseFloat(r.pct) || 5}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Financial health */}
      {(fh.summary || fh.metrics?.length) && (
        <div className="rounded-xl border border-stone-200 bg-white p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-navy">Financial Health</p>
          {fh.summary && <p className="mt-3 text-stone-700">{fh.summary}</p>}
          {fh.metrics?.length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {fh.metrics.map((m, i) => (
                <div key={i} className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{m.label}</p>
                  <p className="tabular mt-1 font-medium text-stone-900">{m.value}</p>
                  <p className={`text-xs font-medium ${m.rating === "Good" ? "text-emerald-600" : m.rating === "Poor" ? "text-rose-600" : "text-amber-600"}`}>{m.rating}</p>
                </div>
              ))}
            </div>
          )}
          {val.summary && <p className="mt-4 text-sm text-stone-600"><span className="font-semibold text-stone-800">Valuation ({val.verdict}): </span>{val.summary}</p>}
        </div>
      )}

      {/* SWOT */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ListCmp title="Strengths" items={swot.strengths} tone="good" />
        <ListCmp title="Weaknesses" items={swot.weaknesses} tone="bad" />
        <ListCmp title="Opportunities" items={swot.opportunities} tone="good" />
        <ListCmp title="Threats" items={swot.threats} tone="bad" />
      </div>

      {/* Market-specific factors */}
      {report.market_specific_factors?.length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-6">
          <p className="mb-2 text-sm font-semibold text-stone-900">Market-specific factors ({report.region})</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {report.market_specific_factors.map((f, i) => (
              <div key={f.label || i} className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                <p className="text-sm font-medium text-stone-800">{f.label}</p>
                <p className="mt-0.5 text-sm text-stone-600">{f.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Thesis */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ListCmp title="Bull case" items={report.bull_case} tone="good" />
        <ListCmp title="Bear case" items={report.bear_case} tone="bad" />
        <ListCmp title="Key risks" items={report.risks} tone="bad" />
        <ListCmp title="Catalysts" items={report.catalysts} tone="good" />
      </div>

      <p className="text-center text-xs text-stone-400">{report.disclaimer}</p>
    </div>
  );
}
